import React, { useMemo } from 'react';

import { cn } from '../../lib/utils';

export const WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME = 'has-workspace-background-image';
// Keep a small set of Image objects alive after decode so the browser can reuse
// their decoded pixels on the route's first paint without retaining every workspace image.
const MAX_PRELOADED_WORKSPACE_BACKGROUND_IMAGES = 8;
const preloadedWorkspaceBackgroundImages = new Map();

export function getWorkspaceBackgroundImageUrl(resource) {
  return resource?.background_image?.variants?.medium?.public_url || resource?.background_image_url || null;
}

export function getWorkspaceBackgroundImageStyle(backgroundImageUrl) {
  return backgroundImageUrl ? { backgroundImage: `url(${JSON.stringify(backgroundImageUrl)})` } : undefined;
}

export function preloadWorkspaceBackgroundImageUrl(backgroundImageUrl) {
  if (!backgroundImageUrl || typeof Image === 'undefined') return Promise.resolve();

  const existingPreload = preloadedWorkspaceBackgroundImages.get(backgroundImageUrl);
  if (existingPreload) {
    preloadedWorkspaceBackgroundImages.delete(backgroundImageUrl);
    preloadedWorkspaceBackgroundImages.set(backgroundImageUrl, existingPreload);
    return existingPreload.promise;
  }

  const image = new Image();
  const loaded = new Promise((resolve) => {
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = backgroundImageUrl;

    if (image.complete) resolve(image.naturalWidth > 0);
  });
  const promise = loaded.then(async (didLoad) => {
    if (!didLoad) {
      preloadedWorkspaceBackgroundImages.delete(backgroundImageUrl);
      return;
    }

    if (typeof image.decode !== 'function') return;

    try {
      await image.decode();
    } catch {
      // A decoded rail/background CSS image can still be paintable when decode()
      // rejects, so failed speculative decoding must never block navigation.
    }
  });

  preloadedWorkspaceBackgroundImages.set(backgroundImageUrl, { image, promise });
  if (preloadedWorkspaceBackgroundImages.size > MAX_PRELOADED_WORKSPACE_BACKGROUND_IMAGES) {
    preloadedWorkspaceBackgroundImages.delete(preloadedWorkspaceBackgroundImages.keys().next().value);
  }

  return promise;
}

export function preloadWorkspaceBackgroundImage(resource) {
  return preloadWorkspaceBackgroundImageUrl(getWorkspaceBackgroundImageUrl(resource));
}

export function useWorkspaceBackgroundImage(resource, enabled = true) {
  const backgroundImageUrl = enabled ? getWorkspaceBackgroundImageUrl(resource) : null;
  const workspaceBackgroundImageStyle = useMemo(() => getWorkspaceBackgroundImageStyle(backgroundImageUrl), [backgroundImageUrl]);

  return {
    backgroundImageUrl,
    hasWorkspaceBackgroundImage: Boolean(backgroundImageUrl),
    workspaceBackgroundImageStyle,
  };
}

export function WorkspaceBackgroundImageLayer({ style, overlayClassName = 'bg-black/40' }) {
  if (!style) return null;

  return (
    <>
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat" style={style} />
      <div aria-hidden className={cn('pointer-events-none absolute inset-0', overlayClassName)} />
    </>
  );
}
